import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as ApiSelectors from '../redux/selectors/api'
import FetchTypes from '../util/fetch-types'
import { isEmpty } from 'lodash'
import { addMemberPharmacy, fetchMemberPharmacies, removeMemberPharmacy } from '../redux/thunks/pharmacies'
import { objectIdPropTypeRequired } from '../proptypes/helpers/object-id'
import { ConfirmationAlert, Loading } from 'ui-components-web'
import useAlert from '../hooks/useAlert'
import * as PharmacyThunks from '../redux/thunks/pharmacies'

const withPharmacyControl = (Child) => {
  const Comp = function PharmacyControlComponent(props) {
    const dispatch = useDispatch()
    const { memberId } = props

    const isRemoving = useSelector(ApiSelectors.isTypeFetching(FetchTypes.removeMemberPharmacy))
    const isFetchingMemberPharmacies = useSelector(ApiSelectors.isTypeFetching(FetchTypes.getMemberPharmacies))
    const isSearchingPharmacies = useSelector(ApiSelectors.isTypeFetching(FetchTypes.getPharmacies))
    const isAddingPharmacies = useSelector(ApiSelectors.isTypeFetching(FetchTypes.addMemberPharmacy))

    const { alert, setAlert, resetAlert } = useAlert()

    const handleFetchMemberPharmacies = useCallback(async () => {
      if (isEmpty(memberId)) return null
      return dispatch(fetchMemberPharmacies(memberId))
    }, [dispatch, memberId])

    const handleFetchPharmacies = useCallback(
      (filter) => {
        return dispatch(PharmacyThunks.fetchPharmacies(filter))
      },
      [dispatch],
    )

    const handleAddPharmacy = useCallback(
      async (pharmacy) => {
        return dispatch(addMemberPharmacy(memberId, pharmacy.id))
      },
      [memberId],
    )

    const handleRemovePharmacy = useCallback(
      (pharmacyId, callback) => async () => {
        const result = await dispatch(removeMemberPharmacy(memberId, pharmacyId))
        resetAlert()
        if (callback) callback(result)
        return result
      },
      [dispatch, memberId, resetAlert],
    )

    const handleConfirmRemove = useCallback(
      async (pharmacyId, callback) => {
        setAlert({
          type: 'custom',
          visible: true,
          body: 'Are you sure you want to remove this pharmacy?',
          onYes: handleRemovePharmacy(pharmacyId, callback),
          onNo: resetAlert,
          yesContent: 'YES, REMOVE PHARMACY',
          noContent: 'NO',
          customNoBtnClass: 'alert-link-no alert-link',
          customYesBtnClass: 'alert-link-yes alert-link',
        })
      },
      [setAlert, handleRemovePharmacy],
    )

    const isPharmacyLoading = isRemoving || isFetchingMemberPharmacies || isSearchingPharmacies || isAddingPharmacies

    return (
      <>
        <Child
          {...props}
          addPharmacy={handleAddPharmacy}
          removePharmacy={handleConfirmRemove}
          fetchPharmacies={handleFetchPharmacies}
          fetchMemberPharmacies={handleFetchMemberPharmacies}
        />
        <ConfirmationAlert {...alert} isAlert={alert.visible} />
        <Loading loading={isPharmacyLoading} />
      </>
    )
  }
  Comp.propTypes = {
    memberId: objectIdPropTypeRequired,
  }
  return Comp
}

export default withPharmacyControl
